import React, { useState, useMemo, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Badge, Form } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { 
  FaCube, 
  FaServer, 
  FaCheckCircle,
  FaArrowRight,
  FaPhone,
  FaExternalLinkAlt,
  FaMicrochip,
  FaMemory,
  FaHdd,
  FaRocket,
  FaCog,
  FaNetworkWired,
  FaShieldAlt,
  FaCloud,
  FaFilter,
  FaSearch,
  FaTh,
  FaList,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import { useScrollAnimation } from '../../../hooks';
import { useLanguage } from '../../../contexts/LanguageContext';
import { Helmet } from 'react-helmet-async';

const SolutionHCI = () => {
  const { t, lang } = useLanguage();

  // Données HCI Dell localisées
  const HCI_DATA = {
    categories: [
      {
        id: 'vxrail',
        name: t.hci.categories.vxrail.name,
        description: t.hci.categories.vxrail.desc,
        icon: <FaCube />
      },
      {
        id: 'azure-local',
        name: t.hci.categories.azure.name,
        description: t.hci.categories.azure.desc,
        icon: <FaCloud />
      },
      {
        id: 'vsan-ready',
        name: t.hci.categories.vsan.name,
        description: t.hci.categories.vsan.desc,
        icon: <FaServer />
      },
      {
        id: 'xc-series',
        name: t.hci.categories.xc.name,
        description: t.hci.categories.xc.desc,
        icon: <FaCog />
      }
    ],
    solutions: [
      {
        id: 'vxrail-integrated',
        name: lang === 'fr' ? 'VxRail - Systèmes intégrés' : 'VxRail - Integrated Systems',
        category: 'vxrail',
        badge: lang === 'fr' ? 'Populaire' : 'Popular',
        isNew: false,
        image: '/images/HCI/vxrail-hero.png',
        imageSize: { width: '90%', height: '100%', objectFit: 'cover' },
        imagePosition: { objectPosition: 'center 100%' },
        workloads: lang === 'fr' 
          ? ['Virtualisation VMware', 'Opérations rationalisées', 'Évolution prévisible']
          : ['VMware Virtualization', 'Streamlined Operations', 'Predictable Evolution'],
        specs: {
          processor: lang === 'fr' ? 'Processeurs Intel® Xeon® Scalable' : 'Intel® Xeon® Scalable Processors',
          memory: lang === 'fr' ? 'Configuration flexible selon besoins' : 'Flexible configuration based on needs',
          storage: lang === 'fr' ? 'Stockage software-defined intégré' : 'Integrated software-defined storage',
          network: lang === 'fr' ? 'Réseau haute performance intégré' : 'Integrated high-performance network'
        },
        features: lang === 'fr'
          ? ['Conçu avec VMware, pour VMware', 'Rationalise les opérations', 'Évolution prévisible', 'Gestion simplifiée']
          : ['Designed with VMware, for VMware', 'Streamlines operations', 'Predictable evolution', 'Simplified management']
      },
      {
        id: 'vmware-cloud-foundation',
        name: lang === 'fr' ? 'VMware Cloud Foundation sur VxRail' : 'VMware Cloud Foundation on VxRail',
        category: 'vxrail', 
        badge: lang === 'fr' ? 'Datacenter SDDC' : 'SDDC Datacenter',
        isNew: false,
        image: '/images/HCI/vmware-cloud-foundation.png',
        imageSize: { width: '100%', height: '100%', objectFit: 'contain' },
        imagePosition: { objectPosition: 'center center' },
        workloads: lang === 'fr'
          ? ['Datacenter software-defined', 'Cloud hybride', 'Transition digitale']
          : ['Software-defined datacenter', 'Hybrid Cloud', 'Digital Transition'],
        specs: {
          processor: lang === 'fr' ? 'Processeurs Intel® Xeon® Scalable' : 'Intel® Xeon® Scalable Processors',
          memory: lang === 'fr' ? 'Mémoire haute capacité' : 'High capacity memory',
          storage: lang === 'fr' ? 'Stockage software-defined VMware' : 'VMware software-defined storage',
          network: lang === 'fr' ? 'Réseau software-defined' : 'Software-defined network'
        },
        features: lang === 'fr'
          ? ['Accélère la transition vers le datacenter software-defined', 'Cloud hybride VMware', 'Infrastructure unifiée', 'Gestion centralisée']
          : ['Accelerates transition to software-defined datacenter', 'VMware Hybrid Cloud', 'Unified infrastructure', 'Centralized management']
      },
      {
        id: 'vxrail-dynamic-nodes',
        name: lang === 'fr' ? 'Nœuds dynamiques VxRail' : 'VxRail Dynamic Nodes',
        category: 'vxrail',
        badge: lang === 'fr' ? 'Flexible' : 'Flexible',
        isNew: false,
        image: '/images/HCI/vxrail-dynamic-nodes.png',
        imageSize: { width: '70%', height: '70%', objectFit: 'cover' },
        imagePosition: { objectPosition: 'left center' },
        workloads: lang === 'fr'
          ? ['Calcul flexible', 'Stockage évolutif', 'Charges variables']
          : ['Flexible compute', 'Scalable storage', 'Variable workloads'],
        specs: {
          processor: lang === 'fr' ? 'Processeurs Intel® Xeon® Scalable' : 'Intel® Xeon® Scalable Processors',
          memory: lang === 'fr' ? 'Évolution indépendante du calcul' : 'Independent compute scaling',
          storage: lang === 'fr' ? 'Stockage software-defined Dell évolutif' : 'Scalable Dell software-defined storage',
          network: lang === 'fr' ? 'Réseau haute performance' : 'High performance network'
        },
        features: lang === 'fr'
          ? ['Évolution flexible du calcul et stockage', 'Évolution indépendante', 'Stockage software-defined Dell', 'Optimisation des ressources']
          : ['Flexible evolution of compute and storage', 'Independent scaling', 'Dell software-defined storage', 'Resource optimization']
      },
      {
        id: 'dell-ax-azure-local',
        name: lang === 'fr' ? 'Système Dell AX pour Azure Local' : 'Dell AX System for Azure Local',
        category: 'azure-local',
        badge: 'Microsoft Premier',
        isNew: true,
        image: '/images/HCI/dell-ax-azure.png',
        imageSize: { width: '90%', height: '90%', objectFit: 'contain' },
        imagePosition: { objectPosition: 'center bottom' },
        workloads: lang === 'fr'
          ? ['Cloud adaptatif Azure', 'Automatisation', 'Intégration améliorée']
          : ['Azure adaptive cloud', 'Automation', 'Enhanced integration'],
        specs: {
          processor: lang === 'fr' ? 'Processeurs Intel® Xeon® Scalable' : 'Intel® Xeon® Scalable Processors',
          memory: lang === 'fr' ? 'Configuration Azure optimisée' : 'Optimized Azure configuration',
          storage: lang === 'fr' ? 'Stockage compatible Azure Local' : 'Azure Local compatible storage',
          network: lang === 'fr' ? 'Réseau haute performance Azure' : 'Azure high performance network'
        },
        features: lang === 'fr'
          ? ['Solution Microsoft Premier', 'Automatisation améliorée', 'Intégration optimisée', 'Innovation avec audace', 'Cloud adaptatif Azure optimisé']
          : ['Microsoft Premier solution', 'Enhanced automation', 'Optimized integration', 'Bold innovation', 'Optimized Azure adaptive cloud']
      },
      {
        id: 'vsan-ready-nodes',
        name: lang === 'fr' ? 'Nœuds vSAN Ready' : 'vSAN Ready Nodes',
        category: 'vsan-ready',
        badge: lang === 'fr' ? 'Modernisation' : 'Modernization',
        isNew: false,
        image: '/images/HCI/vsan-ready-nodes.png',
        imageSize: { width: '90%', height: '100%', objectFit: 'cover' },
        imagePosition: { objectPosition: 'right center' },
        workloads: lang === 'fr'
          ? ['Modernisation datacenter', 'Infrastructure HCI', 'Déploiement accéléré']
          : ['Datacenter modernization', 'HCI Infrastructure', 'Accelerated deployment'],
        specs: {
          processor: lang === 'fr' ? 'Large éventail de serveurs PowerEdge' : 'Wide range of PowerEdge servers',
          memory: lang === 'fr' ? 'Configuration selon modèle PowerEdge' : 'Configuration per PowerEdge model',
          storage: lang === 'fr' ? 'VMware vSAN™ intégré' : 'Integrated VMware vSAN™',
          network: lang === 'fr' ? 'Réseau certifié conjointement' : 'Jointly certified network'
        },
        features: lang === 'fr'
          ? ['Combine serveurs PowerEdge avec VMware vSAN™', 'Solution préconfigurée et certifiée', 'Réduit les risques', 'Accélère le déploiement', 'Modernise le datacenter']
          : ['Combines PowerEdge servers with VMware vSAN™', 'Preconfigured and certified solution', 'Reduces risks', 'Accelerates deployment', 'Modernizes datacenter']
      },
      {
        id: 'xc-series-nutanix',
        name: lang === 'fr' ? 'Solutions de la gamme XC' : 'XC Series Solutions',
        category: 'xc-series',
        badge: lang === 'fr' ? 'Polyvalent' : 'Versatile',
        isNew: false,
        image: '/images/HCI/xc-series-nutanix.png',
        imageSize: { width: '70%', height: '70%', objectFit: 'cover' },
        imagePosition: { objectPosition: 'center center' },
        workloads: lang === 'fr'
          ? ['Infrastructure polyvalente', 'HCI évolutive', 'Multi-hyperviseur']
          : ['Versatile infrastructure', 'Scalable HCI', 'Multi-hypervisor'],
        specs: {
          processor: lang === 'fr' ? 'Serveurs Dell PowerEdge avec processeurs Intel Xeon' : 'Dell PowerEdge servers with Intel Xeon processors',
          memory: lang === 'fr' ? 'Configuration selon modèle PowerEdge' : 'Configuration per PowerEdge model',
          storage: lang === 'fr' ? 'Stockage software-defined Nutanix' : 'Nutanix software-defined storage',
          network: lang === 'fr' ? 'Réseau haute performance' : 'High performance network'
        },
        features: lang === 'fr'
          ? ['Intègrent logiciel Nutanix', 'Serveurs Dell PowerEdge avec processeurs Intel Xeon', 'Plateforme HCI polyvalente et évolutive', 'Une des plus polyvalentes du marché']
          : ['Integrates Nutanix software', 'Dell PowerEdge servers with Intel Xeon processors', 'Versatile and scalable HCI platform', 'One of the most versatile on the market']
      }
    ]
  };

  const [setHeroRef, heroInView] = useScrollAnimation(0.1);
  const [setCategoriesRef, categoriesInView] = useScrollAnimation(0.1);
  const [setSolutionsRef, solutionsInView] = useScrollAnimation(0.1);

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;

  const filteredSolutions = useMemo(() => {
    let filtered = HCI_DATA.solutions;
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(s => s.category === selectedCategory);
    }
    if (searchTerm.trim()) {
      const sLower = searchTerm.toLowerCase();
      filtered = filtered.filter(s => 
        s.name.toLowerCase().includes(sLower) ||
        s.workloads.some(w => w.toLowerCase().includes(sLower)) ||
        s.features.some(f => f.toLowerCase().includes(sLower))
      );
    }
    return filtered;
  }, [selectedCategory, searchTerm, lang]);

  const paginatedSolutions = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredSolutions.slice(start, start + itemsPerPage);
  }, [filteredSolutions, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredSolutions.length / itemsPerPage);

  const handleCategoryChange = useCallback((c) => { setSelectedCategory(c); setCurrentPage(1); }, []);
  const handleSearchChange = useCallback((e) => { setSearchTerm(e.target.value); setCurrentPage(1); }, []);

  return (
    <div>
    
      {/* --- SEO DYNAMIQUE POUR GOOGLE --- */}
      <Helmet>
        <title>{t.hci.seo.title}</title>
        <meta name="description" content={t.hci.seo.description} />
        <link rel="canonical" href="https://www.s2i.com.tn/solutions/hci" />
        
        {/* Balises Open Graph pour LinkedIn (très utilisé pour le HCI) */}
        <meta property="og:title" content={t.hci.seo.title} />
        <meta property="og:description" content={t.hci.seo.description} />
        <meta property="og:image" content="https://www.s2i.com.tn/images/HCI/vxrail-hero.png" />
      </Helmet>
      {/* ---------------------------------- */}

      {/* Hero */}
      <section ref={setHeroRef} className="hero-section-sii" style={{ background: `linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(71, 85, 105, 0.9) 100%), url('/images/HCI/hci-datacenter.jpg')`, backgroundSize: 'cover', minHeight: '80vh', display: 'flex', alignItems: 'center', position: 'relative', overflow: 'hidden' }}>
        <Container style={{ position: 'relative', zIndex: 1 }}>
          <Row className="align-items-center">
            <Col lg={6}>
              <motion.div initial={{ opacity: 0, y: 50 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}>
                <span className="badge-sii mb-4"><FaCube className="me-2" />{t.hci.hero.badge}</span>
                <h1 className="hero-title-sii" style={{ color: '#fff' }} dangerouslySetInnerHTML={{ __html: t.hci.hero.title }} />
                <p className="hero-subtitle-sii" style={{ color: 'rgba(255,255,255,0.9)' }}>{t.hci.hero.subtitle}</p>
                <div className="mt-4">
                  <Button className="btn-sii-primary me-3" size="lg" onClick={() => document.querySelector('[data-solutions-section]')?.scrollIntoView({ behavior: 'smooth' })}>{t.hci.hero.btnExplore} <FaArrowRight className="ms-2" /></Button>
                  <Button variant="outline-light" size="lg" onClick={() => window.location.href = '/#contact'}>{t.hci.hero.btnQuote}</Button>
                </div>
              </motion.div>
            </Col>
            <Col lg={6}>
              <motion.div initial={{ opacity: 0, x: 50 }} animate={heroInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8, delay: 0.4 }} className="text-center">
                <img src="/images/HCI/vxrail-hero1.jpg" alt="Dell HCI" style={{ width: '100%', maxWidth: 500, borderRadius: 16, boxShadow: '0 20px 40px rgba(0,0,0,0.3)' }} />
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Categories */}
      <section ref={setCategoriesRef} className="py-5 bg-white">
        <Container>
          <div className="text-center mb-5">
            <h2 className="section-title-sii">{t.hci.categories.title}</h2>
            <p className="section-subtitle-sii">{t.hci.categories.subtitle}</p>
          </div>
          <Row className="g-4">
            {HCI_DATA.categories.map((cat, idx) => (
              <Col lg={3} md={6} key={cat.id}>
                <motion.div initial={{ opacity: 0, y: 30 }} animate={categoriesInView ? { opacity: 1, y: 0 } : {}} transition={{ delay: idx * 0.1 }} className="p-4 text-center h-100" style={{ background: '#f8f9fa', borderRadius: '16px' }}>
                  <div style={{ fontSize: '2.5rem', color: '#d90000', marginBottom: '1rem' }}>{cat.icon}</div>
                  <h4 style={{ fontWeight: 700 }}>{cat.name}</h4>
                  <p className="text-muted small">{cat.description}</p>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Solutions Section */}
      <section className="py-5" data-solutions-section>
        <Container fluid>
          <motion.div ref={setSolutionsRef} initial={{ opacity: 0 }} animate={solutionsInView ? { opacity: 1 } : {}}>
            <Row>
              {/* Sidebar */}
              <Col lg={3}>
                <div className="p-4 bg-white border rounded-4 shadow-sm sticky-top" style={{ top: '100px' }}>
                  <h5 className="fw-bold mb-4"><FaFilter className="me-2 text-danger" />{t.hci.sidebar.title}</h5>
                  <Form.Group className="mb-4">
                    <Form.Label className="small fw-bold text-uppercase">{t.hci.sidebar.search}</Form.Label>
                    <div className="position-relative">
                      <FaSearch className="position-absolute translate-middle-y top-50 start-0 ms-3 text-muted" />
                      <Form.Control type="text" className="ps-5" placeholder={t.hci.sidebar.searchPl} value={searchTerm} onChange={handleSearchChange} />
                    </div>
                  </Form.Group>
                  <Form.Group className="mb-4">
                    <Form.Label className="small fw-bold text-uppercase">{t.hci.sidebar.category}</Form.Label>
                    <div className="d-grid gap-2">
                      <Button variant={selectedCategory === 'all' ? 'danger' : 'outline-secondary'} onClick={() => handleCategoryChange('all')} className="text-start">{t.hci.sidebar.all}</Button>
                      {HCI_DATA.categories.map(c => (
                        <Button key={c.id} variant={selectedCategory === c.id ? 'danger' : 'outline-secondary'} onClick={() => handleCategoryChange(c.id)} className="text-start">{c.name}</Button>
                      ))}
                    </div>
                  </Form.Group>
                  <div className="text-center p-3 bg-light rounded-3 mb-4">
                    <span className="fw-bold text-danger">{filteredSolutions.length}</span> {t.hci.sidebar.results}
                  </div>
                  <div className="btn-group w-100">
                    <Button variant={viewMode === 'grid' ? 'danger' : 'outline-secondary'} onClick={() => setViewMode('grid')}><FaTh /></Button>
                    <Button variant={viewMode === 'list' ? 'danger' : 'outline-secondary'} onClick={() => setViewMode('list')}><FaList /></Button>
                  </div>
                </div>
              </Col>

              {/* Main Content */}
              <Col lg={9}>
                {paginatedSolutions.length > 0 ? (
                  viewMode === 'grid' ? (
                    <Row className="g-4">
                      {paginatedSolutions.map(s => (
                        <Col md={6} key={s.id}>
                          <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden">
                            <div className="p-4 text-center bg-light position-relative" style={{ height: '200px' }}>
                              <div className="position-absolute top-0 start-0 p-3">
                                {s.isNew && <Badge bg="success" className="me-2">{t.hci.card.new}</Badge>}
                                {s.badge && <Badge bg="danger">{s.badge}</Badge>}
                              </div>
                              {s.image ? <img src={s.image} alt={s.name} style={{ maxHeight: '100%', maxWidth: '80%' }} /> : <FaCube size="4rem" className="text-danger mt-4" />}
                            </div>
                            <Card.Body className="p-4">
                              <h5 className="fw-bold mb-3">{s.name}</h5>
                              <div className="mb-3">
                                <p className="small fw-bold text-uppercase mb-2 text-muted">{t.hci.card.usage}</p>
                                <div className="d-flex flex-wrap gap-1">{s.workloads.map((w, i) => <Badge key={i} bg="light" text="dark" className="border">{w}</Badge>)}</div>
                              </div>
                              <div className="mb-3">
                                <p className="small fw-bold text-uppercase mb-2 text-muted">{t.hci.card.specs}</p>
                                <div className="small mb-1"><FaMicrochip className="me-2" />{s.specs.processor}</div>
                                <div className="small mb-1"><FaMemory className="me-2" />{s.specs.memory}</div>
                                <div className="small"><FaHdd className="me-2" />{s.specs.storage}</div>
                              </div>
                              <p className="small fw-bold text-uppercase mb-2 text-muted">{t.hci.card.points}</p>
                              {s.features.slice(0,3).map((f, i) => <div key={i} className="small mb-1 text-success"><FaCheckCircle className="me-2" />{f}</div>)}
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  ) : (
                    <div className="d-flex flex-column gap-4">
                      {paginatedSolutions.map(s => (
                        <Card key={s.id} className="border-0 shadow-sm rounded-4 overflow-hidden">
                          <Card.Body className="p-4">
                            <Row className="align-items-center">
                              <Col md={3} className="text-center">
                                {s.image ? <img src={s.image} alt={s.name} className="img-fluid" style={{ maxHeight: '120px' }} /> : <FaCube size="3rem" className="text-danger" />}
                              </Col>
                              <Col md={4}>
                                <h5 className="fw-bold">{s.name}</h5>
                                <Badge bg="primary" className="mb-2">{s.category}</Badge>
                                <div className="small text-muted">{s.workloads.join(' • ')}</div>
                              </Col>
                              <Col md={5} className="bg-light p-3 rounded-4">
                                <div className="small mb-1"><FaMicrochip className="me-2" />{s.specs.processor}</div>
                                <div className="small"><FaHdd className="me-2" />{s.specs.storage}</div>
                              </Col>
                            </Row>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  )
                ) : (
                  <div className="text-center py-5">
                    <FaSearch size="3rem" className="text-muted mb-3" />
                    <h4>{t.hci.noResults.title}</h4>
                    <Button variant="danger" className="mt-2" onClick={() => { setSelectedCategory('all'); setSearchTerm(''); }}>{t.hci.noResults.btn}</Button>
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center mt-5 gap-2">
                    <Button variant="outline-primary" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}><FaChevronLeft /></Button>
                    {[...Array(totalPages)].map((_, i) => (
                      <Button key={i} variant={currentPage === i + 1 ? 'danger' : 'outline-secondary'} onClick={() => setCurrentPage(i + 1)}>{i + 1}</Button>
                    ))}
                    <Button variant="outline-primary" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}><FaChevronRight /></Button>
                  </div>
                )}
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* CTA */}
      <section className="py-5" style={{ background: '#f8fafc' }}>
        <Container>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} className="p-5 text-center text-white rounded-5 shadow-lg" style={{ background: '#1e293b' }}>
            <h3 className="fw-bold mb-3">{t.hci.cta.title}</h3>
            <p className="opacity-75 mb-4 mx-auto" style={{ maxWidth: '700px' }}>{t.hci.cta.subtitle}</p>
            <div className="d-flex justify-content-center gap-3">
              <Button variant="light" size="lg" onClick={() => window.location.href = '/#contact'}><FaPhone className="me-2" />{t.hci.cta.btnContact}</Button>
              <Button variant="outline-light" size="lg"><FaExternalLinkAlt className="me-2" />{t.hci.cta.btnDoc}</Button>
            </div>
          </motion.div>
        </Container>
      </section>
    </div>
  );
};

export default SolutionHCI;